import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider'
import { OpenFeature } from '@openfeature/react-sdk'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryObserver } from '@tanstack/react-query'

import env from '../env'

const FEATURE_FLAG_REFRESH_INTERVAL_MS = 10 * 60 * 1000
const FEATURE_FLAG_REFRESH_QUERY_KEY = ['feature-flag-refresh'] as const

// Setup OpenFeature with Flagsmith provider
export async function initializeFeatureFlags(): Promise<FlagsmithClientProvider | undefined> {
    if (!env.VITE_FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY) {
        if (env.VITE_FLAG_PROVIDER === undefined /* means flagsmith */) {
            console.warn(
                'VITE_FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY is required for feature flags. When working locally, add them to `.env.docker-compose.local`'
            )
        }
        return
    }

    const provider = new FlagsmithClientProvider({
        environmentID: env.VITE_FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY,
        api: env.VITE_FLAGSMITH_API_URL || 'https://flagsmith.core.deepinsight.io/api/v1/',
        enableAnalytics: false,
        cacheFlags: true,
    })

    if (env.VITE_ENABLE_SENTRY && env.VITE_SENTRY_DSN) {
        OpenFeature.addHooks(new Sentry.OpenFeatureIntegrationHook())
    }

    try {
        // This is what connects the Flagsmith client to the OpenFeature client
        // So that if you call OpenFeature's useFlag hook, it will use the Flagsmith client to fetch the flag value
        await OpenFeature.setProviderAndWait(provider)

        console.info('Feature flags initialized successfully with Flagsmith')
    } catch (error) {
        console.error('Error setting feature flags provider:', error)
    }

    return provider
}

export function subscribeToFeatureFlagRefresh(queryClient: QueryClient, provider: FlagsmithClientProvider): () => void {
    const observer = new QueryObserver(queryClient, {
        queryKey: FEATURE_FLAG_REFRESH_QUERY_KEY,
        // Flag values stay owned by Flagsmith and OpenFeature. React Query only schedules refreshes.
        queryFn: async () => {
            await provider.flagsmithClient.getFlags()
            return null
        },
        initialData: null,
        initialDataUpdatedAt: Date.now(),
        refetchInterval: FEATURE_FLAG_REFRESH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        retry: false,
        staleTime: FEATURE_FLAG_REFRESH_INTERVAL_MS,
    })

    return observer.subscribe(() => undefined)
}
